import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Save } from 'lucide-react';
interface GlobalSettingsProps {
  onBack: () => void;
}
export function GlobalSettings({
  onBack
}: GlobalSettingsProps) {
  const [settings, setSettings] = useState({
    timeIntervalEnabled: true,
    referenceTemperature: 7,
    inspectionsPerSample: 10
  });
  const [isSaved, setIsSaved] = useState(false);
  const handleSave = () => {
    // In production, save to backend/localStorage
    console.log('Saving settings:', settings);
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
  };
  return <div className="min-h-screen w-full bg-white" data-id="element-51">
      <div className="max-w-4xl mx-auto px-8 py-8" data-id="element-52">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} data-id="element-53">
          {/* Header */}
          <div className="flex items-center justify-between mb-8" data-id="element-54">
            <div className="flex items-center gap-4" data-id="element-55">
              <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Zurück" data-id="element-56">
                <ArrowLeft className="w-5 h-5 text-gray-600" strokeWidth={2} data-id="element-57" />
              </button>
              <h1 className="text-3xl font-semibold text-gray-900" data-id="element-58">
                Globale Einstellungen
              </h1>
            </div>

            <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors" data-id="element-59">
              <Save className="w-4 h-4" strokeWidth={2} data-id="element-60" />
              Speichern
            </button>
          </div>

          {/* Success Message */}
          {isSaved && <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg" data-id="element-61">
              <p className="text-sm text-green-800 font-medium" data-id="element-62">
                Einstellungen erfolgreich gespeichert
              </p>
            </motion.div>}

          {/* Settings Form */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-6" data-id="element-63">
            <div data-id="element-64">
              <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-65">
                Standardwerte für neue Produkte
              </h2>
              <p className="text-sm text-gray-600 mb-6" data-id="element-66">
                Diese Werte werden als Standardwerte für neue Produkte verwendet
              </p>

              <div className="space-y-6" data-id="element-67">
                {/* Time Interval Toggle */}
                <div className="border border-gray-200 rounded-lg p-4" data-id="element-68">
                  <div className="flex items-start justify-between" data-id="element-69">
                    <div className="flex-1" data-id="element-70">
                      <label className="block text-sm font-medium text-gray-900 mb-1" data-id="element-71">
                        Zeitliches Prüfintervall
                      </label>
                      <p className="text-sm text-gray-600" data-id="element-72">
                        Standardmäßig aktiviert oder deaktiviert für neue
                        Produkte
                      </p>
                    </div>
                    <div className="ml-4" data-id="element-73">
                      <button onClick={() => setSettings({
                      ...settings,
                      timeIntervalEnabled: !settings.timeIntervalEnabled
                    })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.timeIntervalEnabled ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-74">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.timeIntervalEnabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-75" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-3" data-id="element-76">
                    <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${settings.timeIntervalEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-77">
                      {settings.timeIntervalEnabled ? 'Aktiviert' : 'Deaktiviert'}
                    </span>
                  </div>
                </div>

                {/* Reference Temperature */}
                <div data-id="element-78">
                  <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-79">
                    Referenztemperatur (°C)
                  </label>
                  <input type="number" value={settings.referenceTemperature} onChange={e => setSettings({
                  ...settings,
                  referenceTemperature: parseFloat(e.target.value)
                })} className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-80" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-81">
                    Standardwert für die Referenztemperatur bei neuen Produkten
                  </p>
                </div>

                {/* Inspections Per Sample */}
                <div data-id="element-82">
                  <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-83">
                    Anzahl der Prüfungen pro Stichprobe
                  </label>
                  <input type="number" value={settings.inspectionsPerSample} onChange={e => setSettings({
                  ...settings,
                  inspectionsPerSample: parseInt(e.target.value)
                })} className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-84" />
                  <p className="text-xs text-gray-500 mt-1" data-id="element-85">
                    Standardanzahl der Prüfungen pro Stichprobe für neue
                    Produkte
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-86">
            <p className="text-sm text-blue-800" data-id="element-87">
              <strong data-id="element-88">Hinweis:</strong> Diese Einstellungen gelten nur für neu
              angelegte Produkte. Bestehende Produkte behalten ihre
              individuellen Einstellungen.
            </p>
          </div>
        </motion.div>
      </div>
    </div>;
}